import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, MessageSquareWarning, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatDays, formatMinutes, formatMoney, formatNumber, formatShortTime } from '../lib/format';
import { OBJECTION_TONE, formatWorkDate, minutesBeforeEnd, type ObjectionStatus } from './Violations';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  statusTone,
  useToast,
} from '../components/ui';

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

interface PenaltyDetail {
  id: string;
  userId: string;
  /** Days of salary; null on a penalty raised as a flat amount, before days. */
  days: string | number | null;
  amount: string | number;
  currency: string;
  reason: string;
  sourceType: string | null;
  status: 'PENDING' | 'APPROVED' | 'APPLIED_TO_PAYROLL' | 'CANCELLED';
  effectiveMonth: string;
  /** The day more than an hour short an administrator decided with this penalty (CR-004). */
  shortfallCase: { id: string; status: string; deductionPercent: number | null; shortfallMinutes: number } | null;
  approvedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
  user: Person;
  approvedBy: Person | null;
  cancelledBy: Person | null;
  violation: {
    id: string;
    type: string;
    deadlineAt: string | null;
    actualActionAt: string | null;
    status: string;
    ticket: { id: string; number: string; subject: string } | null;
  } | null;
  attendanceBreak: {
    id: string;
    startedAt: string;
    endedAt: string | null;
    breakType: { id: string; name: string; nameAr: string | null; maxMinutes: number | null };
  } | null;
  idleSession: {
    id: string;
    sessionId: string;
    reason: string;
    lastActivityAt: string;
    startedAt: string;
    endedAt: string | null;
  } | null;
  /** A day worked short of its Daily hours (2026-10-15). */
  dayReview: {
    id: string;
    workDate: string;
    requiredMinutes: number | null;
    workedMinutes: number | null;
    /** SHIFT: its Standard hours (since 2026-10-21); SALARY_RECORD: a day judged before. */
    hoursSource: 'SHIFT' | 'SALARY_RECORD' | 'DEFAULT' | null;
  } | null;
  /**
   * A day whose Regular Shift ended earlier than its shift allows (2026-11-03): when it
   * ended, its rostered end, and the minutes before it the shift allowed.
   */
  earlyEndReview?: {
    id: string;
    workDate: string;
    earlyEndAt: string | null;
    earlyEndRosterEndAt: string | null;
    earlyEndAllowedMinutes: number | null;
  } | null;
  /** The day whose Daily hours were worked, when that dropped this penalty. */
  waivedBy: { id: string; workDate: string; requiredMinutes: number | null; workedMinutes: number | null } | null;
  /** The employee cancelled it with wallet points (CR-WALLET-001). */
  walletRedemption: { id: string; pointsSpent: number; createdAt: string } | null;
  /** The employee's objection to it, and an administrator's answer (2026-10-22). */
  objection: {
    id: string;
    reason: string;
    status: ObjectionStatus;
    createdAt: string;
    decidedAt: string | null;
    decisionNote: string | null;
    decidedBy: Person | null;
  } | null;
  can: { approve: boolean; cancel: boolean; object: boolean; decideObjection: boolean };
}

/**
 * Route: `/violations/penalties/:id` — one penalty: whose, what it is for (a
 * break past its limit, a stretch of inactivity, a day short of its Daily hours,
 * a shift ended earlier than it allows, a ticket's missed deadline),
 * its days and money, and who decided it; and deciding it, for whoever may.
 * A notification about a penalty opens here (operator, 2026-10-11: "i must go to
 * details for this notification not main page").
 */
export function PenaltyDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: penalty, isLoading, error, refetch } = useQuery({
    queryKey: ['penalties', 'one', id],
    queryFn: () => api.get<PenaltyDetail>(`/penalties/${id}`),
  });

  const approve = useMutation({
    mutationFn: () => api.post(`/penalties/${id}/approve`),
    onSuccess: () => {
      toast.success(t('violations.approvedToast'));
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !penalty) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const own = penalty.userId === user?.id;
  const back = penalty.can.approve || penalty.can.decideObjection ? '/violations?tab=review' : '/violations';
  const brk = penalty.attendanceBreak;
  const idle = penalty.idleSession;
  const short = penalty.dayReview;
  // A shift ended earlier than it allows (2026-11-03), with what the day's judgement found.
  const early = penalty.earlyEndReview?.earlyEndAt && penalty.earlyEndReview.earlyEndRosterEndAt ? penalty.earlyEndReview : null;
  const violation = penalty.violation;
  const waived = penalty.waivedBy;
  // An administrator's decision on a day more than an hour short (CR-004) is a short day too.
  const adminDecision = penalty.sourceType === 'ATTENDANCE_SHORTFALL';
  const title: TranslationKey = brk
    ? 'violations.kind.break'
    : idle
      ? 'violations.kind.idle'
      : adminDecision
        ? 'violations.kind.shortfall'
      : short
        ? 'violations.kind.shortDay'
        : early
          ? 'violations.kind.earlyEnd'
          : violation
            ? 'violations.kind.sla'
            : 'violations.kind.other';
  const decided =
    penalty.status === 'CANCELLED' ? penalty.cancelledBy : penalty.status !== 'PENDING' ? penalty.approvedBy : null;
  const decidedAt = penalty.status === 'CANCELLED' ? penalty.cancelledAt : penalty.approvedAt;

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('violations.title')}
        </Link>
      </div>

      <PageHeader
        title={t(title)}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={statusTone(penalty.status)}>{enumLabel(penalty.status, locale)}</Badge>
            {!own && <span>{localized(penalty.user)}</span>}
            {penalty.days != null && <span className="tabular text-error-fg dark:text-error-dark">{formatDays(penalty.days, locale)}</span>}
          </span>
        }
        action={
          (penalty.can.approve || penalty.can.cancel || penalty.can.object || penalty.can.decideObjection) && (
            <>
              {/* An objection waiting is decided first; its penalty's Approve and Cancel step aside until then. */}
              {penalty.can.decideObjection && (
                <>
                  <Button
                    variant="primary"
                    icon={<Check className="h-4 w-4" />}
                    onClick={() => navigate(`/violations/penalties/${penalty.id}/objection/approve`)}
                  >
                    {t('violations.approveObjection')}
                  </Button>
                  <Button
                    variant="danger"
                    icon={<X className="h-4 w-4" />}
                    onClick={() => navigate(`/violations/penalties/${penalty.id}/objection/reject`)}
                  >
                    {t('violations.rejectObjection')}
                  </Button>
                </>
              )}
              {penalty.can.object && (
                <Button
                  icon={<MessageSquareWarning className="h-4 w-4" />}
                  onClick={() => navigate(`/violations/penalties/${penalty.id}/object`)}
                >
                  {t('violations.object')}
                </Button>
              )}
              {penalty.can.approve && (
                <Button variant="primary" icon={<Check className="h-4 w-4" />} loading={approve.isPending} onClick={() => approve.mutate()}>
                  {t('common.approve')}
                </Button>
              )}
              {penalty.can.cancel && (
                <Button disabled={approve.isPending} onClick={() => navigate(`/violations/penalties/${penalty.id}/cancel`)}>
                  {t('violations.cancel')}
                </Button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          {penalty.objection && <ObjectionCard penalty={penalty} own={own} />}

          {/* An approved objection cancelled it: the objection's card below says who and why. */}
          {penalty.status === 'CANCELLED' && (waived || penalty.cancellationReason) && penalty.objection?.status !== 'ACCEPTED' && (
            <Card className="border-primary/30">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {penalty.cancelledBy
                  ? t('violations.cancelledBy', { name: localized(penalty.cancelledBy) })
                  : t('violations.cancelReason')}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {waived
                  ? t('violations.waived', {
                      worked: formatMinutes(waived.workedMinutes, locale),
                      owed: formatMinutes(waived.requiredMinutes, locale),
                      date: formatWorkDate(waived.workDate, locale),
                    })
                  : penalty.walletRedemption
                    ? t('violations.walletCancelled', { points: formatNumber(penalty.walletRedemption.pointsSpent, locale) })
                    : penalty.cancellationReason}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('violations.forWhat')} />
            <dl className="divide-y divide-[var(--line)]">
              {brk ? (
                <>
                  <Detail label={t('violations.breakType')}>{localized(brk.breakType)}</Detail>
                  <Detail label={t('violations.started')}>
                    <span className="tabular">{formatDateTime(brk.startedAt, locale)}</span>
                  </Detail>
                  {brk.endedAt && (
                    <Detail label={t('violations.length')}>
                      <span className="tabular">
                        {brk.breakType.maxMinutes != null
                          ? t('violations.breakLength', {
                              length: formatMinutes(minutesBetween(brk.startedAt, brk.endedAt), locale),
                              limit: formatMinutes(brk.breakType.maxMinutes, locale),
                            })
                          : formatMinutes(minutesBetween(brk.startedAt, brk.endedAt), locale)}
                      </span>
                    </Detail>
                  )}
                </>
              ) : short ? (
                <>
                  <Detail label={t('violations.workDate')}>
                    <span className="tabular">{formatWorkDate(short.workDate, locale)}</span>
                  </Detail>
                  <Detail label={t('violations.owed')}>
                    <span
                      className="tabular"
                      title={short.hoursSource === 'DEFAULT' ? t('violations.owedFromDefault') : undefined}
                    >
                      {formatMinutes(short.requiredMinutes, locale)}
                    </span>
                  </Detail>
                  <Detail label={t('violations.worked')}>
                    <span className="tabular text-error-fg dark:text-error-dark">{formatMinutes(short.workedMinutes, locale)}</span>
                  </Detail>
                  {adminDecision && penalty.days != null && (
                    <Detail label={t('shortfall.decisionCol')}>
                      {t('violations.shortfallDecision', { percent: Math.round(Number(penalty.days) * 100) })}
                      {/* The case itself is the administrators' page. */}
                      {penalty.shortfallCase && can(P.PENALTIES_MANAGE) && (
                        <Link
                          to={`/violations/short-days/${penalty.shortfallCase.id}`}
                          className="mt-0.5 block text-caption text-primary-600 hover:underline dark:text-primary-400"
                        >
                          {t('violations.openShortDay')}
                        </Link>
                      )}
                    </Detail>
                  )}
                </>
              ) : early ? (
                <>
                  <Detail label={t('violations.workDate')}>
                    <span className="tabular">{formatWorkDate(early.workDate, locale)}</span>
                  </Detail>
                  <Detail label={t('violations.shiftEnds')}>
                    <span className="tabular">{formatShortTime(early.earlyEndRosterEndAt, locale)}</span>
                  </Detail>
                  <Detail label={t('violations.endedAt')}>
                    <span className="tabular text-error-fg dark:text-error-dark">{formatShortTime(early.earlyEndAt, locale)}</span>
                  </Detail>
                  <Detail label={t('violations.earlyBy')}>
                    <span className="tabular">
                      {formatMinutes(minutesBeforeEnd(early.earlyEndAt!, early.earlyEndRosterEndAt!), locale)}
                    </span>
                  </Detail>
                  {early.earlyEndAllowedMinutes != null && (
                    <Detail label={t('violations.earlyAllowed')}>
                      {t('violations.earlyAllowedValue', { minutes: formatMinutes(early.earlyEndAllowedMinutes, locale) })}
                    </Detail>
                  )}
                </>
              ) : idle ? (
                <>
                  <Detail label={t('attendance.reason')}>{t(`presence.reason.${idle.reason}` as TranslationKey)}</Detail>
                  <Detail label={t('attendance.lastActivity')}>
                    <span className="tabular">{formatDateTime(idle.lastActivityAt, locale)}</span>
                  </Detail>
                  {idle.endedAt && (
                    <Detail label={t('violations.length')}>
                      <span className="tabular">{formatMinutes(minutesBetween(idle.startedAt, idle.endedAt), locale)}</span>
                    </Detail>
                  )}
                  <Detail label={t('violations.idleShift')}>
                    <Link to={`/attendance/sessions/${idle.sessionId}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                      {t('violations.openShift')}
                    </Link>
                  </Detail>
                </>
              ) : violation ? (
                <>
                  <Detail label={t('violations.type')}>{enumLabel(violation.type, locale)}</Detail>
                  {violation.ticket && (
                    <Detail label={t('nav.tickets')}>
                      <Link to={`/tickets/${violation.ticket.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        <span className="font-mono text-mono-sm">{violation.ticket.number}</span>
                        <span className="block text-caption" dir="auto">
                          {violation.ticket.subject}
                        </span>
                      </Link>
                    </Detail>
                  )}
                  {violation.deadlineAt && (
                    <Detail label={t('violations.deadline')}>
                      <span className="tabular">{formatDateTime(violation.deadlineAt, locale)}</span>
                    </Detail>
                  )}
                  <Detail label={t('violations.actualAction')}>
                    {violation.actualActionAt ? (
                      <span className="tabular">{formatDateTime(violation.actualActionAt, locale)}</span>
                    ) : (
                      <span className="text-error-fg dark:text-error-dark">—</span>
                    )}
                  </Detail>
                  {violation.status === 'VOIDED' && (
                    <Detail label={t('common.status')}>
                      <Badge>{enumLabel(violation.status, locale)}</Badge>
                    </Detail>
                  )}
                </>
              ) : null}
            </dl>
            {!brk && !idle && !short && !early && (
              <div className={violation ? 'mt-4 border-t hairline pt-4' : undefined}>
                <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.reason')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {penalty.reason}
                </p>
              </div>
            )}
          </Card>
        </div>

        <div className="min-w-0">
          <Card>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.employee')}>{localized(penalty.user)}</Detail>
              <Detail label={t('violations.days')}>
                <span className="tabular text-error-fg dark:text-error-dark">{penalty.days != null ? formatDays(penalty.days, locale) : '—'}</span>
              </Detail>
              <Detail label={t('common.amount')}>
                <PenaltyMoney penalty={penalty} />
              </Detail>
              <Detail label={t('violations.effectiveMonth')}>
                <span className="tabular" dir="ltr">
                  {penalty.effectiveMonth}
                </span>
              </Detail>
              <Detail label={t('violations.raised')}>
                <span className="tabular">{formatDateTime(penalty.createdAt, locale)}</span>
              </Detail>
              {decided && (
                <Detail label={t('violations.decidedBy')}>
                  <Stacked
                    first={localized(decided)}
                    second={decidedAt ? formatDateTime(decidedAt, locale) : null}
                  />
                </Detail>
              )}
              {penalty.status === 'APPLIED_TO_PAYROLL' && (
                <Detail label={t('nav.payroll')}>{t('violations.appliedToPayroll')}</Detail>
              )}
            </dl>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * The employee's objection (2026-10-22): why, and when — and how it stands: waiting
 * for an administrator, who decides it before the penalty itself; approved or
 * rejected, with their reason; or closed, the penalty cancelled some other way.
 */
function ObjectionCard({ penalty, own }: { penalty: PenaltyDetail; own: boolean }) {
  const { t, locale, localized } = useI18n();
  const objection = penalty.objection!;
  const decided = objection.status === 'ACCEPTED' || objection.status === 'REJECTED';
  const note =
    objection.status === 'CLOSED'
      ? t('violations.objectionClosed')
      : objection.status === 'PENDING' && penalty.can.decideObjection
        ? t('violations.objectionFirst')
        : objection.status === 'PENDING' && own
          ? t('violations.objectionYours')
          : null;
  return (
    <Card className={objection.status === 'PENDING' ? 'border-warning/40' : undefined}>
      <CardHeader
        title={t('violations.objection')}
        action={
          <Badge tone={OBJECTION_TONE[objection.status]}>
            {t(`violations.objectionState.${objection.status}` as TranslationKey)}
          </Badge>
        }
      />
      <p className="mb-1 text-caption text-[var(--text-tertiary)]">
        {t('violations.objectionFrom', { name: localized(penalty.user) })}
        {' · '}
        <span className="tabular">{formatDateTime(objection.createdAt, locale)}</span>
      </p>
      <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
        {objection.reason}
      </p>

      {decided && (
        <div className="mt-4 border-t hairline pt-4">
          <p className="mb-1 text-caption text-[var(--text-tertiary)]">
            {objection.decidedBy
              ? t(objection.status === 'ACCEPTED' ? 'violations.objectionApprovedBy' : 'violations.objectionRejectedBy', {
                  name: localized(objection.decidedBy),
                })
              : t(`violations.objectionState.${objection.status}` as TranslationKey)}
            {objection.decidedAt && (
              <>
                {' · '}
                <span className="tabular">{formatDateTime(objection.decidedAt, locale)}</span>
              </>
            )}
          </p>
          <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
            {objection.decisionNote}
          </p>
        </div>
      )}
      {note && <p className="mt-4 border-t hairline pt-4 text-body-sm text-muted">{note}</p>}
    </Card>
  );
}

/** Its money: what payroll took, or an estimate at the day rate until then — or no salary on record that day. */
function PenaltyMoney({ penalty }: { penalty: PenaltyDetail }) {
  const { t, locale } = useI18n();
  const estimate = penalty.days != null && penalty.status !== 'APPLIED_TO_PAYROLL';
  if (estimate && Number(penalty.amount) === 0) {
    return (
      <span className="text-warning-fg dark:text-warning-dark" title={t('violations.noSalaryHint')}>
        {t('violations.noSalary')}
      </span>
    );
  }
  return (
    <span className="tabular" title={estimate ? t('violations.estimate') : undefined}>
      {estimate && '≈ '}
      {formatMoney(penalty.amount, penalty.currency, locale)}
    </span>
  );
}

function Stacked({ first, second }: { first: ReactNode; second: ReactNode }) {
  return (
    <span className="inline-flex flex-col items-end">
      <span>{first}</span>
      {second && <span className="text-caption text-muted tabular">{second}</span>}
    </span>
  );
}

function minutesBetween(from: string, to: string): number {
  return Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 60_000);
}
